import { useCallback, useState } from 'react';
import { useAuth } from '../context/AuthContext';

function read(storageKey: string | null): string | null {
  if (!storageKey) return null;
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

/**
 * A choice a person makes on a screen — which view, which tab — remembered in
 * this browser, per person (`<key>:<userId>`, like `recent-events:<userId>`),
 * so the screen opens where they left it next time. Only a value among
 * `options` is ever answered: a remembered view since removed falls back.
 * Storage that is blocked or full just forgets; the screen works regardless.
 */
export function useRememberedChoice<T extends string>(
  key: string,
  options: readonly T[],
  fallback: T,
): [T, (next: T) => void] {
  const { user } = useAuth();
  const storageKey = user?.id ? `${key}:${user.id}` : null;
  const [stored, setStored] = useState<{ key: string | null; value: string | null }>(() => ({
    key: storageKey,
    value: read(storageKey),
  }));
  // Another person signing in on this browser reads their own choice.
  const value = stored.key === storageKey ? stored.value : read(storageKey);
  const current = options.includes(value as T) ? (value as T) : fallback;

  const remember = useCallback(
    (next: T) => {
      setStored({ key: storageKey, value: next });
      if (!storageKey) return;
      try {
        window.localStorage.setItem(storageKey, next);
      } catch {
        // Nothing to do: the choice holds for this visit only.
      }
    },
    [storageKey],
  );
  return [current, remember];
}
